import { type PointerEvent, useCallback, useEffect, useRef, useState } from "react";
import { Sheet } from "../../../shared/phone/Sheet";
import type { PartnerSignatureInput } from "../../../shared/types";
import { Icon } from "./Icon";

type Stroke = { x: number; y: number }[];

/** The ink a signature is saved in, the same as the computer's: it reads on the owner's white paper. */
const INK = "#12245e";
const PAD = 12;

function draw(ctx: CanvasRenderingContext2D, strokes: Stroke[], color: string, dx = 0, dy = 0): void {
  ctx.strokeStyle = color;
  ctx.lineWidth = 2.6;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  for (const s of strokes) {
    if (s.length === 0) continue;
    ctx.beginPath();
    ctx.moveTo(s[0].x - dx, s[0].y - dy);
    if (s.length === 1) ctx.lineTo(s[0].x - dx + 0.1, s[0].y - dy);
    for (const p of s.slice(1)) ctx.lineTo(p.x - dx, p.y - dy);
    ctx.stroke();
  }
}

/** The drawing as a PNG, cropped to the ink with a little room, so it fills its line wherever it was drawn. */
function toPng(strokes: Stroke[]): string {
  const pts = strokes.flat();
  const minX = Math.min(...pts.map((p) => p.x)) - PAD;
  const minY = Math.min(...pts.map((p) => p.y)) - PAD;
  const w = Math.max(...pts.map((p) => p.x)) - minX + PAD;
  const h = Math.max(...pts.map((p) => p.y)) - minY + PAD;
  const scale = 2;
  const c = document.createElement("canvas");
  c.width = Math.ceil(w * scale);
  c.height = Math.ceil(h * scale);
  const ctx = c.getContext("2d");
  if (!ctx) return "";
  ctx.scale(scale, scale);
  draw(ctx, strokes, INK, minX, minY);
  return c.toDataURL("image/png");
}

function at(e: PointerEvent<HTMLCanvasElement>): { x: number; y: number } {
  const r = e.currentTarget.getBoundingClientRect();
  return { x: e.clientX - r.left, y: e.clientY - r.top };
}

/**
 * Signing on a phone: a sheet with a full-width pad drawn on with a finger, and the line confirming it is
 * the partner's legal signature. Done hands back a drawn signature under the partner's name.
 */
export function SignSheet({
  title,
  name,
  onClose,
  onAdopt,
}: {
  title: string;
  name: string;
  onClose: () => void;
  onAdopt: (sig: PartnerSignatureInput) => void;
}) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const strokes = useRef<Stroke[]>([]);
  const drawing = useRef(false);
  const [inked, setInked] = useState(false);
  const [agreed, setAgreed] = useState(false);

  const paint = useCallback(() => {
    const c = canvas.current;
    const ctx = c?.getContext("2d");
    if (!c || !ctx) return;
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, c.width, c.height);
    draw(ctx, strokes.current, getComputedStyle(c).color);
  }, []);

  // Sized to the pad as laid out, at the screen's pixel density so the line stays sharp.
  useEffect(() => {
    const c = canvas.current;
    if (!c) return;
    const fit = () => {
      const r = c.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      c.width = Math.round(r.width * dpr);
      c.height = Math.round(r.height * dpr);
      paint();
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(c);
    return () => ro.disconnect();
  }, [paint]);

  return (
    <Sheet
      open
      onClose={onClose}
      title={title}
      left={
        <button type="button" className="pn-tbtn" onClick={onClose}>
          Cancel
        </button>
      }
      foot={
        <div className="pn-btns">
          <button
            type="button"
            className="pn-btn"
            onClick={() => {
              strokes.current = [];
              setInked(false);
              paint();
            }}
          >
            Clear
          </button>
          <button type="button" className="pn-btn pri" disabled={!inked || !agreed} onClick={() => onAdopt({ kind: "drawn", name, dataUrl: toPng(strokes.current) })}>
            Done
          </button>
        </div>
      }
    >
      <div className="po-padw">
        <canvas
          ref={canvas}
          className="po-pad"
          role="img"
          aria-label="Draw your signature here"
          onPointerDown={(e) => {
            e.currentTarget.setPointerCapture(e.pointerId);
            drawing.current = true;
            strokes.current.push([at(e)]);
            setInked(true);
            paint();
          }}
          onPointerMove={(e) => {
            if (!drawing.current) return;
            strokes.current[strokes.current.length - 1].push(at(e));
            paint();
          }}
          onPointerUp={() => {
            drawing.current = false;
          }}
          onPointerCancel={() => {
            drawing.current = false;
          }}
        />
        {inked ? null : <span className="po-pad-ph">Sign with your finger</span>}
        <i className="po-pad-ln" />
      </div>
      <label className="po-check">
        <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
        <span className="po-box">
          <Icon name="check" />
        </span>
        <span>I agree this is my legal signature.</span>
      </label>
    </Sheet>
  );
}
